import { Skeleton, Flex } from '@cycle-app/ui';
import { CloseIcon, InfoIconOutline } from '@cycle-app/ui/icons';
import { toShortLocaleDateString } from '@cycle-app/utilities';
import { useState } from 'react';
import { Route, Switch } from 'react-router-dom';

import { DocPanelWithBoardConfig } from 'src/app/Main/Board/DocPanel/DocPanel';
import { ReleasesPageTitle } from 'src/components/PageTitle/PageTitle';
import { ReleaseDocInsights } from 'src/components/ReleaseNotes/ReleaseDocInsights';
import { ReleaseDocStatus } from 'src/components/ReleaseNotes/ReleaseDocStatus';
import { ReleaseNoteDraftTag } from 'src/components/ReleaseNotes/ReleaseNoteDraftTag';
import { RELEASE_NOTE_EDITOR_CONTAINER } from 'src/constants/releases.constants';
import { PageId, routing } from 'src/constants/routing.constant';
import { ReleaseNotesProvider } from 'src/contexts/releaseNotesContext';
import { useFeatureFlag, useNavigate } from 'src/hooks';
import { useRelease } from 'src/hooks/releases/useRelease';
import { useReleaseNote } from 'src/hooks/releases/useReleaseNote';
import { useParams } from 'src/hooks/router/useParams';
import { useGetPermission } from 'src/reactives';

import { ReleaseDocTag } from '../ReleaseDocTag';
import {
  Container, ReleaseNoteHeader, HeaderContent, Left, Right, Nav, Action,
  Separator, Date, EditorContainer, EditorHeader, Alert, StyledDocPanelRealtime,
} from './ReleaseNote.styles';
import { ReleaseNoteCopyContent } from './ReleaseNoteCopyContent';
import { ReleaseNoteDocOpen } from './ReleaseNoteDocOpen';
import { ReleaseNoteEditor } from './ReleaseNoteEditor';
import { ReleaseNoteEditorTitle } from './ReleaseNoteEditorTitle';
import { ReleaseNotePrevNext } from './ReleaseNotePrevNext';

const useCloseReleaseNote = () => {
  const { releaseId } = useParams();
  const { navigateToRelease } = useNavigate();
  return () => {
    if (!releaseId) return;
    navigateToRelease(releaseId);
  };
};

export const ReleaseNote = () => {
  const { canUpdateReleaseNote } = useGetPermission();
  const {
    releaseId, noteId,
  } = useParams();
  const {
    release, isReleaseLoading, isReadonly,
  } = useRelease(releaseId);
  const {
    releaseNote, isReleaseNoteLoading,
  } = useReleaseNote(noteId);
  const [isHovered, setIsHovered] = useState(false);
  const closeReleaseNote = useCloseReleaseNote();
  const isChangelogEnabled = useFeatureFlag('changelog').isEnabled;
  if (!noteId || !releaseId || !release) return null;

  const isLoading = isReleaseLoading || isReleaseNoteLoading;
  const releaseDate = toShortLocaleDateString(release.date);

  return (
    <>
      {releaseNote?.title && (
        <ReleasesPageTitle
          releaseDate={releaseDate}
          releaseNoteTitle={releaseNote?.title}
        />
      )}
      <Container
        initial="hidden"
        animate="visible"
        exit="hidden"
        variants={{
          visible: {
            opacity: 1,
            transition: {
              duration: 0.1,
              delay: 0.1,
            },
          },
          hidden: {
            opacity: 0,
            transition: {
              duration: 0.1,
            },
          },
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onBlur={() => setIsHovered(false)}
      >
        <EditorContainer className={RELEASE_NOTE_EDITOR_CONTAINER}>
          <EditorHeader>
            <ReleaseNoteAlert releaseNoteId={noteId} />
            <ReleaseNoteEditorTitle
              releaseNoteId={noteId}
              isReadOnly={isReadonly || !canUpdateReleaseNote}
            />
          </EditorHeader>

          <ReleaseNoteEditor
            releaseNoteId={noteId}
            isReadOnly={isReadonly || !canUpdateReleaseNote}
          />
        </EditorContainer>

        <ReleaseNoteHeader>
          {isLoading ? (
            <HeaderSkeleton />
          ) : (
            <HeaderContent>
              <Left>
                <Nav>
                  <Action onClick={closeReleaseNote}>
                    <CloseIcon />
                  </Action>
                  <Separator />
                  <ReleaseNotesProvider isReadonly={isReadonly} releaseId={releaseId}>
                    <ReleaseNotePrevNext />
                  </ReleaseNotesProvider>
                </Nav>
                {!isChangelogEnabled && release?.date && <Date>{toShortLocaleDateString(release.date)}</Date>}
                {!isChangelogEnabled && noteId && <ReleaseDocTag releaseId={releaseId} noteId={noteId} />}
              </Left>

              {isChangelogEnabled && noteId && (
                <Flex $gap={12}>
                  <StyledDocPanelRealtime />
                  <ReleaseNoteDocOpen releaseId={releaseId} noteId={noteId} />
                  <ReleaseNoteCopyContent />
                </Flex>
              )}
              {!isChangelogEnabled && noteId && (
                <Right>
                  <ReleaseDocStatus noteId={noteId} isEnabled={isHovered} isReadOnly={!canUpdateReleaseNote} />
                  <ReleaseNoteDraftTag noteId={noteId} />
                  <ReleaseNoteCopyContent />
                  <ReleaseDocInsights noteId={noteId} isReadOnly={!canUpdateReleaseNote} />
                </Right>
              )}
            </HeaderContent>
          )}
        </ReleaseNoteHeader>

        <Switch>
          <Route path={routing[PageId.ReleaseDoc]}>
            <DocPanelWithBoardConfig />
          </Route>
        </Switch>
      </Container>
    </>
  );
};

const ReleaseNoteAlert = ({ releaseNoteId }: { releaseNoteId: string }) => {
  const { releaseNote } = useReleaseNote(releaseNoteId);
  if (!releaseNote?.isOther) return null;
  return (
    <Alert>
      <p>
        <InfoIconOutline />
      </p>
      <p>
        {'This release note is in the '}
        <strong>Other improvements & bugs section</strong>
        , the description will not appear in the public change log, only the title.
      </p>
    </Alert>
  );
};

const HeaderSkeleton = () => {
  const closeReleaseNote = useCloseReleaseNote();
  return (
    <HeaderContent>
      <Left>
        <Action onClick={closeReleaseNote}>
          <CloseIcon />
        </Action>
        <Skeleton height={20} width={80} />
        <Skeleton height={20} width={60} />
      </Left>
      <Right>
        <Skeleton height={20} width={60} />
        <Skeleton height={20} width={60} />
      </Right>
    </HeaderContent>
  );
};
